<template>
  <div>
    <p v-if="regionalCreditScoreGrade">{{ $t('message.credit.alternateCreditVisionInfo') }}</p>
    <Notification :text="$t('message.credit.notification')"> </Notification>
    <div>
      <CreditResultDetails class="mt-4" :creditRange="shownGrade"> </CreditResultDetails>
      <template v-if="prequalLoading === false">
        <!-- if we have prequal results -->
        <section v-if="preQualificationLoansExist" class="mt-4 pt-2 prequal-loans-section">
          <h3>{{ prequalHeading }}<sup>*</sup></h3>
          <p>{{ $t('message.credit.application.preQualification.msg', { dealerName: getBusinessName }) }}</p>
          <template v-if="!isHiddenPrequal">
            <finance-lender-card
              :lender-key="getPrequalLenderCategory(preQualificationResult.providerKey)"
              :lender-name="preQualificationResult.providerName"
              :loan-amount="preQualificationResult.amountUpToInDollars"
              :interest-rate="preQualificationResult.rateAsPercent"
              :monthly-payment="preQualificationResult.paymentUpToInDollars"
              :loan-term-in-months="preQualificationResult.termUpToInMonths"
              @lender-selected="lenderSelected(preQualificationResult.providerKey)"
              class="mt-4"
              data-test="financeLenderCard"
            ></finance-lender-card>
            <pre-qualification-disclaimer data-test="pqDisclaimer" class="mt-3" />
          </template>
          <template v-else-if="financeApplicationEnabled">
            <ApplyNow class="mb-4" data-test="applyNowCta"></ApplyNow>
            <pre-qualification-disclaimer data-test="pqDisclaimer" class="mt-3" />
          </template>
        </section>
        <!-- if we don't have prequal results or have a credit score < 500 -->
        <template v-else class="mt-4 pb-3">
          <pre-qualification-disclaimer data-test="pqDisclaimer" class="mt-3" />
          <h3 :class="{ 'mb-3': !isKijijiDealer }" data-test="prequalNeedMoreInfo">
            {{ $t('message.credit.application.preQualification.needMoreInfo.title') }}
          </h3>
          <div v-if="isKijijiDealer">
            <p>
              {{ $t('message.credit.application.preQualification.needMoreInfo.body', { dealerName: getBusinessName }) }}
            </p>
            <p class="font-size-10 color-primary-grey">
              {{ $t('message.credit.application.preQualification.needMoreInfo.msgKijiji') }}
            </p>
          </div>
          <ApplyNow v-else class="mb-4"></ApplyNow>
          <p class="font-size-10 color-primary-grey">
            {{ $t('message.credit.application.preQualification.needMoreInfo.msgEquifax') }}
          </p>
        </template>
        <!-- disclaimers -->
        <template v-if="preQualificationLoansExist">
          <div class="lender-card-disclaimers" data-test="prequalDisclamiers">
            <p class="mb-3 disclaimer-heading">
              {{
                $t(
                  `message.credit.disclaimer.lenderDisclaimerHeading.${getPrequalLenderCategory(
                    preQualificationResult.providerKey
                  )}`,
                  { dealer: getBusinessName }
                )
              }}
            </p>
            <div
              class="font-size-10 color-primary-grey"
              v-html-safe="
                isKijijiDealer
                  ? $t(
                      `message.credit.disclaimer.lenderDisclaimerKijiji.${getPrequalLenderCategory(
                        preQualificationResult.providerKey
                      )}`,
                      { dealer: getBusinessName }
                    )
                  : $t(
                      `message.credit.disclaimer.lenderDisclaimer.${getPrequalLenderCategory(
                        preQualificationResult.providerKey
                      )}`,
                      { dealer: getBusinessName }
                    )
              "
            ></div>
          </div>
        </template>
      </template>
      <template v-if="prequalLoading">
        <div class="text-center">
          <img width="250" src="@/assets/loading.gif" alt="" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PrequalFinanceProviderCodes } from '@/utils/enums';
import CreditResultDetails from '@/components/credit-results/CreditResultDetails.vue';
import FinanceLenderCard from '@/components/credit/finance-lender/FinanceLenderCard.vue';
import Notification from '@/components/shared/Notification.vue';
import ApplyNow from '@/components/credit/ApplyNow.vue';
import PreQualificationDisclaimer from '@/components/credit-results/PreQualificationDisclaimer.vue';

export default {
  name: 'PrequalCreditResults',
  components: {
    PreQualificationDisclaimer,
    ApplyNow,
    CreditResultDetails,
    FinanceLenderCard,
    Notification,
  },
  data() {
    return {
      prequalLoading: true,
      prequalFinanceProviderCodes: PrequalFinanceProviderCodes,
    };
  },
  computed: {
    ...mapGetters('credit', [
      'creditScoreGrade',
      'preQualificationLoansExist',
      'preQualificationResult',
      'regionalCreditScoreGrade',
    ]),
    ...mapGetters('business', ['financeApplicationEnabled', 'getBusinessName', 'isKijijiDealer']),
    ...mapGetters('user', ['getLanguageCodeForNumericalInternationalization']),
    shownGrade() {
      return this.creditScoreGrade ? this.creditScoreGrade : this.regionalCreditScoreGrade;
    },
    preQualificationResultLenderKey() {
      return this.preQualificationLoansExist
        ? this.getPrequalLenderCategory(this.preQualificationResult.providerKey)
        : null;
    },
    prequalifiedAmount() {
      return this.preQualificationLoansExist && !this.isHiddenPrequal
        ? this.$n(
            this.preQualificationResult.amountUpToInDollars,
            'currencyDollars',
            this.getLanguageCodeForNumericalInternationalization
          )
        : null;
    },
    isHiddenPrequal() {
      return !!(this.preQualificationResult && this.preQualificationResult.hidePrequal);
    },
    prequalHeading() {
      if (this.preQualificationResultLenderKey === PrequalFinanceProviderCodes.TD && !this.isHiddenPrequal) {
        return this.$t('message.credit.application.preQualification.TD.prequalNotHiddenTitle', {
          dealerName: this.getBusinessName,
          amount: this.prequalifiedAmount,
        });
      }
      return this.$t(`message.credit.application.preQualification.${this.preQualificationResultLenderKey}.title`);
    },
  },
  methods: {
    lenderSelected(lenderKey) {
      this.$store.commit('credit/SET_SELECTED_PRE_QUALIFICATION_LOAN', lenderKey);
      this.goToFinance();
    },
    goToFinance() {
      this.goToAVFinanceFlow();
    },
    getPrequalLenderCategory(lenderKey) {
      switch (lenderKey) {
        case PrequalFinanceProviderCodes.TD:
        case PrequalFinanceProviderCodes.TD_RV:
        case PrequalFinanceProviderCodes.TD_MARINE:
        case PrequalFinanceProviderCodes.TD_POWERSPORTS:
          return PrequalFinanceProviderCodes.TD;
        default:
          return PrequalFinanceProviderCodes.GL;
      }
    },
  },
  async mounted() {
    try {
      if (!this.preQualificationLoansExist) {
        await this.$store.dispatch('credit/getPreQualificationLoans');
      }
    } catch (err) {
      this.$log('Error in beforeCreate credit results page', err);
    } finally {
      this.prequalLoading = false;
    }
    try {
      await this.$store.dispatch('lead/updateLead');
    } catch (err) {
      this.$log('Error in update lead credit results page', err);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

.disclaimer-heading {
  color: $primary-dark-text;
}

.lender-card-disclaimers {
  margin-top: 32px;
}
@media (max-width: $breakpoint-mobile) {
  .prequal-loans-section {
    margin-top: 0 !important;
    padding-top: 0.5rem !important;
  }
}
</style>
